import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const InfluencerDashboard = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('sessionToken');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/influencer/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load influencer dashboard:', e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!data || data.codes?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('influencer.noAccess', 'No Influencer Access')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            {t('influencer.noAccessDesc', 'You don\'t have any promo codes linked to your account yet. Contact the admin to get started.')}
          </p>
        </div>
      </div>
    );
  }

  const summary = data.summary;

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : ''}`} data-testid="influencer-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {t('influencer.title', 'Influencer Dashboard')}
          </h1>
          <p className="text-purple-200 mt-1 text-sm sm:text-base">
            {t('influencer.subtitle', 'Track your promo codes, earnings, and performance')}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="influencer-summary-cards">
          {[
            { label: t('influencer.activeCodes', 'Active Codes'), value: summary.active_codes, color: 'text-green-600 dark:text-green-400' },
            { label: t('influencer.totalUses', 'Total Uses'), value: summary.total_uses, color: 'text-blue-600 dark:text-blue-400' },
            { label: t('influencer.revenueDriven', 'Revenue Driven'), value: `${summary.total_revenue_driven.toFixed(2)} KD`, color: 'text-purple-600 dark:text-purple-400' },
            { label: t('influencer.myEarnings', 'My Earnings'), value: `${summary.total_earnings.toFixed(2)} KD`, color: 'text-amber-600 dark:text-amber-400' },
            { label: t('influencer.totalPaid', 'Total Paid'), value: `${(summary.total_paid || 0).toFixed(2)} KD`, color: 'text-green-700 dark:text-green-400' },
            { label: t('influencer.pendingBalance', 'Pending Balance'), value: `${(summary.pending_balance || 0).toFixed(2)} KD`, color: (summary.pending_balance || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400' },
            { label: t('influencer.discountsGiven', 'Discounts Given'), value: `${summary.total_discount_given.toFixed(2)} KD`, color: 'text-red-500' },
            { label: t('influencer.totalCodes', 'Total Codes'), value: summary.total_codes, color: 'text-gray-700 dark:text-gray-300' },
          ].map((s, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{s.label}</p>
              <p className={`text-lg sm:text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* My Promo Codes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="influencer-codes-table">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('influencer.myCodes', 'My Promo Codes')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.code', 'Code')}</th>
                  <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.discount', 'Discount')}</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">{t('influencer.uses', 'Uses')}</th>
                  <th className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.revenue', 'Revenue')}</th>
                  <th className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.earnings', 'My Earnings')}</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">{t('influencer.status', 'Status')}</th>
                  <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.expiry', 'Expiry')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.codes.map(p => {
                  const isExpired = p.expires_at && p.expires_at < new Date().toISOString().split('T')[0];
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-testid={`influencer-code-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded text-sm">{p.code}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900 dark:text-white">{p.discount_value}{p.discount_type === 'percentage' ? '%' : ' KD'}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">{p.discount_type}</span>
                      </td>
                      <td className="text-center px-4 py-3">
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full text-xs font-medium">
                          {p.total_uses || 0}{p.max_total_uses ? `/${p.max_total_uses}` : ''}
                        </span>
                      </td>
                      <td className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 font-medium text-gray-900 dark:text-white`}>
                        {(p.total_booking_revenue || 0).toFixed(2)} KD
                      </td>
                      <td className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 text-amber-600 dark:text-amber-400 font-bold`}>
                        {(p.total_influencer_earnings || 0).toFixed(2)} KD
                      </td>
                      <td className="text-center px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isExpired ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : p.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {isExpired ? t('influencer.expired', 'Expired') : p.is_active ? t('influencer.active', 'Active') : t('influencer.inactive', 'Inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {p.expires_at || t('influencer.noExpiry', 'No expiry')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Usage */}
        {(data.recent_usage || []).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="influencer-recent-usage">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {t('influencer.recentUsage', 'Recent Code Usage')}
              </h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {data.recent_usage.map((u, i) => (
                <div key={i} className={`flex items-center justify-between px-5 py-3 text-sm`}>
                  <div className={`flex items-center gap-3`}>
                    <span className="font-mono text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded text-xs">{u.code}</span>
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('influencer.bookingAmount', 'Booking')}: {u.booking_amount} KD
                    </span>
                  </div>
                  <div className={`flex gap-4 text-xs`}>
                    <span className="text-red-500">-{u.discount_amount} KD</span>
                    {u.influencer_earning > 0 && (
                      <span className="text-amber-600 dark:text-amber-400 font-medium">+{u.influencer_earning} KD</span>
                    )}
                    <span className="text-gray-400 dark:text-gray-500">{new Date(u.used_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payout History */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="influencer-payout-history">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('influencer.payoutHistory', 'Payout History')}
            </h2>
            {(summary.pending_balance || 0) > 0 && (
              <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                {t('influencer.pendingAmount', 'Pending')}: {(summary.pending_balance || 0).toFixed(2)} KD
              </span>
            )}
          </div>
          {(data.payouts || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.payoutDate', 'Date')}</th>
                    <th className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.payoutAmount', 'Amount')}</th>
                    <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.payoutMethod', 'Method')}</th>
                    <th className={`${isRTL ? 'text-right' : 'text-left'} px-4 py-3 font-semibold text-gray-600 dark:text-gray-300`}>{t('influencer.payoutRef', 'Reference')}</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">{t('influencer.payoutStatus', 'Status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.payouts.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-testid={`payout-row-${i}`}>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className={`${isRTL ? 'text-left' : 'text-right'} px-4 py-3 font-bold text-green-700 dark:text-green-400`}>{p.amount.toFixed(2)} KD</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          {p.payment_method === 'bank_transfer' ? t('influencer.bankTransfer', 'Bank Transfer') : p.payment_method === 'cash' ? t('influencer.cash', 'Cash') : p.payment_method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs font-mono">{p.reference_number || '—'}</td>
                      <td className="text-center px-4 py-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          {t('influencer.paid', 'Paid')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-8 text-center text-gray-400 dark:text-gray-500">
              {t('influencer.noPayouts', 'No payouts recorded yet. Your earnings will be paid by the admin team.')}
            </div>
          )}
        </div>

        {/* Payout Info */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-5">
          <div className={`flex items-start gap-3`}>
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                {t('influencer.payoutInfo', 'About Payouts')}
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {t('influencer.payoutInfoDesc', 'Earnings shown here are tracked for reference. Actual payouts are processed by the admin team separately. Contact admin for payout inquiries.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerDashboard;
